import { useState, useEffect } from "react";
import { Link } from "../lib/router";
import { PLATFORMS, getPlatformInfo } from "../lib/social";
import { PlatformIcon } from "../components/PlatformIcon";
import { RevealSection, useTypingEffect, useCountUp } from "../components/LandingAnimations";

export function Home() {
  return (
    <>
      <HeroSection />
      <PlatformBar />
      <FeaturesOverview />
      <StatsSection />
      <FeatureDeepDive />
      <TestimonialsSection />
      <FinalCTA />
      <LandingFooter />
    </>
  );
}

/* ─── Hero ─── */

function HeroSection() {
  const typed = useTypingEffect(
    ["Twitter", "Instagram", "Facebook", "LinkedIn", "TikTok", "YouTube", "Reddit", "Pinterest", "Telegram"],
    90, 50, 1800
  );

  return (
    <section className="relative overflow-x-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/[0.07] via-amber-500/[0.04] to-transparent dark:from-orange-500/[0.05] dark:via-amber-500/[0.02] animate-gradient" />
      <div className="absolute inset-0 bg-grid" />

      {/* Content — two-column on desktop */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-20 sm:pt-24 md:pt-32 pb-16 sm:pb-20 md:pb-28">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left: headline + CTAs */}
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-semibold mb-6 sm:mb-8 border border-orange-200/60 dark:border-orange-500/20 animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
              Unified Social Publishing
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-stone-900 dark:text-white mb-5 sm:mb-6 tracking-tight leading-[1.1] animate-fade-up" style={{ animationDelay: "0.1s" }}>
              Publish to{" "}
              <span className="text-gradient inline-block min-w-[120px] sm:min-w-[140px] md:min-w-[180px] text-left">{typed}</span>
              <span className="animate-blink text-orange-500">|</span>
              <br />
              from one place.
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-stone-500 dark:text-zinc-400 mb-8 sm:mb-10 max-w-lg mx-auto md:mx-0 leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
              Connect all your social accounts into a single dashboard.
              Write once, publish everywhere, track everything.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <Link href="/register" className="px-6 sm:px-8 py-3.5 sm:py-4 bg-orange-500 text-white rounded-2xl font-semibold text-base sm:text-lg hover:bg-orange-600 transition-all animate-pulse-glow shadow-lg shadow-orange-500/20 text-center">
                Start Publishing Free
              </Link>
              <Link href="/features" className="px-6 sm:px-8 py-3.5 sm:py-4 bg-white dark:bg-zinc-800 text-stone-700 dark:text-zinc-200 rounded-2xl font-semibold text-base sm:text-lg border border-stone-200 dark:border-zinc-700 hover:bg-stone-50 dark:hover:bg-zinc-750 transition-colors text-center">
                See Features
              </Link>
            </div>
          </div>

          {/* Right: animated publish demo */}
          <div className="animate-fade-up" style={{ animationDelay: "0.4s" }}>
            <HeroDemo />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Hero Publish Demo ─── */

const DEMO_TEXT = "Just launched our new product! Check it out and let us know what you think. 🚀";
const CYCLE_MS = 8000; // full animation cycle

function HeroDemo() {
  const [phase, setPhase] = useState<"typing" | "publishing" | "delivered">("typing");
  const [textLen, setTextLen] = useState(0);
  const [delivered, setDelivered] = useState<number[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let t: ReturnType<typeof setTimeout> | undefined;

    function runCycle() {
      // Phase 1: type text
      setPhase("typing");
      setTextLen(0);
      setDelivered([]);

      let charIdx = 0;
      function typeNext() {
        if (charIdx < DEMO_TEXT.length) {
          charIdx++;
          setTextLen(charIdx);
          t = setTimeout(typeNext, 30 + Math.random() * 30);
        } else {
          // Phase 2: publish
          t = setTimeout(() => {
            setPhase("publishing");
            // Phase 3: deliver one by one
            let platIdx = 0;
            function deliverNext() {
              if (platIdx < PLATFORMS.length) {
                setDelivered(prev => [...prev, platIdx]);
                platIdx++;
                t = setTimeout(deliverNext, 250);
              } else {
                setPhase("delivered");
                // Wait then restart
                t = setTimeout(runCycle, 2500);
              }
            }
            t = setTimeout(deliverNext, 400);
          }, 600);
        }
      }
      t = setTimeout(typeNext, 800);
    }

    runCycle();
    return () => {
      if (t) clearTimeout(t);
    };
  }, []);

  const isPublishing = phase === "publishing" || phase === "delivered";
  const showText = DEMO_TEXT.slice(0, textLen);

  return (
    <div>
      {/* Compose Card */}
      <div className="mx-auto w-full max-w-md rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 shadow-2xl shadow-stone-900/5 dark:shadow-black/20 p-5">
        <div className="flex items-center gap-2 mb-3">
          <img src="/brand/logo-20260908.png" width={32} height={32} alt="" aria-hidden="true" className="w-8 h-8 shrink-0 rounded-lg" />
          <span className="text-sm font-semibold text-stone-900 dark:text-white">New Post</span>
          {phase === "delivered" && (
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 font-semibold animate-fade-in">
              Published
            </span>
          )}
          {phase === "publishing" && (
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 font-semibold animate-pulse">
              Publishing...
            </span>
          )}
        </div>

        {/* Text area */}
        <div className="bg-stone-50 dark:bg-zinc-800 rounded-xl p-3 mb-3 min-h-[56px]">
          <p className="text-sm text-stone-700 dark:text-zinc-300 leading-relaxed">
            {showText}
            {phase === "typing" && <span className="animate-blink text-orange-500 ml-0.5">|</span>}
          </p>
        </div>

        {/* Publish button */}
        <div className={`h-10 rounded-xl flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
          isPublishing
            ? "bg-green-500 text-white"
            : textLen > 0
              ? "bg-orange-500 text-white"
              : "bg-stone-200 dark:bg-zinc-700 text-stone-400 dark:text-zinc-500"
        }`}>
          {phase === "delivered" ? (
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
              Sent to 9 platforms
            </span>
          ) : phase === "publishing" ? (
            <span className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Publishing...
            </span>
          ) : (
            "Publish"
          )}
        </div>
      </div>

      {/* Platform delivery row */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-5 sm:mt-6">
        {PLATFORMS.map((p, i) => {
          const info = getPlatformInfo(p.id);
          const isDelivered = delivered.includes(i);
          return (
            <div key={p.id} className="flex flex-col items-center gap-1.5">
              <div className={`relative w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-300 ${
                isDelivered
                  ? `${info.color} text-white shadow-lg scale-110`
                  : "bg-stone-200 dark:bg-zinc-800 text-stone-400 dark:text-zinc-600"
              }`}>
                <PlatformIcon platform={p.id} className="w-4 h-4 sm:w-5 sm:h-5" />
                {isDelivered && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-zinc-950 animate-scale-in">
                    <svg className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-medium transition-colors duration-300 hidden sm:block ${
                isDelivered ? "text-stone-700 dark:text-zinc-300" : "text-stone-300 dark:text-zinc-700"
              }`}>
                {p.name.split(" ")[0]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Platform Showcase ─── */

function PlatformBar() {
  return (
    <RevealSection className="py-16 border-t border-stone-200/40 dark:border-zinc-800/40">
      <p className="text-center text-[10px] uppercase font-semibold text-stone-400 dark:text-zinc-600 tracking-[0.2em] mb-8">
        Publish to 9 platforms simultaneously
      </p>
      <div className="flex flex-wrap justify-center gap-6 md:gap-10 max-w-4xl mx-auto px-4">
        {PLATFORMS.map((p, i) => {
          const info = getPlatformInfo(p.id);
          return (
            <div key={p.id} className={`flex flex-col items-center gap-2 group reveal reveal-delay-${Math.min(i + 1, 5)}`}>
              <span className={`w-12 h-12 ${info.color} text-white rounded-2xl flex items-center justify-center shadow-sm transition-transform group-hover:scale-110 group-hover:-translate-y-1`}>
                <PlatformIcon platform={p.id} className="w-6 h-6" />
              </span>
              <span className="text-xs font-medium text-stone-500 dark:text-zinc-500 group-hover:text-stone-900 dark:group-hover:text-zinc-200 transition-colors">
                {p.name}
              </span>
            </div>
          );
        })}
      </div>
    </RevealSection>
  );
}

/* ─── Features Overview ─── */

const FEATURE_CARDS = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    title: "Unified Publishing",
    description: "Write your content once and publish it across Twitter, Instagram, Facebook, LinkedIn, TikTok, YouTube, Reddit, Pinterest, and Telegram simultaneously.",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: "Analytics & Insights",
    description: "Track engagement, reach, and growth across all platforms. Detailed per-post analytics and page-level insights with beautiful charts.",
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
      </svg>
    ),
    title: "API & Developer Tools",
    description: "Full REST API with project-scoped API keys. Automate your publishing workflow or integrate SocialCast into your existing tools.",
  },
];

function FeaturesOverview() {
  return (
    <section className="py-24 px-4">
      <RevealSection className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white tracking-tight">
          Everything you need to <span className="text-gradient">dominate social</span>
        </h2>
        <p className="text-stone-500 dark:text-zinc-400 mt-4 max-w-xl mx-auto">
          A complete toolkit for managing your social presence across every major platform.
        </p>
      </RevealSection>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {FEATURE_CARDS.map((f, i) => (
          <RevealSection key={i}>
            <div className={`p-8 rounded-3xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full reveal-delay-${i + 1}`}>
              <div className="w-12 h-12 rounded-2xl bg-orange-50 dark:bg-orange-500/10 text-orange-500 flex items-center justify-center mb-5">
                {f.icon}
              </div>
              <h3 className="text-lg font-bold text-stone-900 dark:text-white mb-3">{f.title}</h3>
              <p className="text-sm text-stone-500 dark:text-zinc-400 leading-relaxed">{f.description}</p>
            </div>
          </RevealSection>
        ))}
      </div>
    </section>
  );
}

/* ─── Stats Section ─── */

function StatNumber({ target, suffix = "", label }: { target: number; suffix?: string; label: string }) {
  const { count, ref } = useCountUp(target);
  return (
    <div className="text-center">
      <p className="text-4xl md:text-5xl font-extrabold text-gradient">
        <span ref={ref}>{count.toLocaleString()}</span>{suffix}
      </p>
      <p className="text-sm text-stone-400 dark:text-zinc-500 uppercase tracking-widest mt-2 font-semibold">{label}</p>
    </div>
  );
}

function StatsSection() {
  return (
    <section className="py-20 bg-stone-900 dark:bg-zinc-950 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto px-4">
        <StatNumber target={10000} suffix="+" label="Active Creators" />
        <StatNumber target={9} label="Platforms" />
        <StatNumber target={1000000} suffix="+" label="Posts Published" />
        <StatNumber target={99} suffix=".9%" label="Uptime" />
      </div>
    </section>
  );
}

/* ─── Feature Deep-Dive ─── */

function FeatureDeepDive() {
  return (
    <section className="py-24 max-w-5xl mx-auto px-4 space-y-32">
      {/* Block 1 */}
      <div className="flex flex-col md:flex-row items-center gap-12">
        <RevealSection direction="left" className="md:w-1/2">
          <div className="rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 p-6 shadow-sm">
            {/* Compose mockup */}
            <div className="space-y-3">
              <div className="h-3 w-24 bg-stone-200 dark:bg-zinc-700 rounded" />
              <div className="h-20 bg-stone-100 dark:bg-zinc-800 rounded-lg border border-stone-200/60 dark:border-zinc-700/60 p-3">
                <div className="h-2 w-3/4 bg-stone-300 dark:bg-zinc-600 rounded" />
                <div className="h-2 w-1/2 bg-stone-200 dark:bg-zinc-700 rounded mt-2" />
              </div>
              <div className="flex gap-2">
                {PLATFORMS.slice(0, 5).map(p => {
                  const info = getPlatformInfo(p.id);
                  return (
                    <span key={p.id} className={`w-7 h-7 ${info.color} text-white rounded-lg flex items-center justify-center`}>
                      <PlatformIcon platform={p.id} className="w-3.5 h-3.5" />
                    </span>
                  );
                })}
                <span className="w-7 h-7 bg-stone-200 dark:bg-zinc-700 rounded-lg flex items-center justify-center text-xs text-stone-500 dark:text-zinc-400">+4</span>
              </div>
              <div className="h-9 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xs font-semibold">Publish to 9 Platforms</span>
              </div>
            </div>
          </div>
        </RevealSection>
        <RevealSection direction="right" className="md:w-1/2">
          <h3 className="text-2xl md:text-3xl font-bold text-stone-900 dark:text-white mb-4">Write Once, Publish Everywhere</h3>
          <p className="text-stone-500 dark:text-zinc-400 leading-relaxed mb-4">
            Compose your post in our editor, select your accounts, and hit publish. SocialCast handles the rest &mdash; formatting, media optimization, and per-platform adjustments.
          </p>
          <ul className="space-y-2 text-sm text-stone-600 dark:text-zinc-300">
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Multi-image and video support</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Per-platform text overrides</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Real-time publish status tracking</li>
          </ul>
        </RevealSection>
      </div>

      {/* Block 2 */}
      <div className="flex flex-col md:flex-row-reverse items-center gap-12">
        <RevealSection direction="right" className="md:w-1/2">
          <div className="rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 p-6 shadow-sm">
            {/* Analytics mockup */}
            <div className="space-y-3">
              <div className="flex gap-3">
                {["Reach", "Engagement", "Followers"].map((m, i) => (
                  <div key={m} className="flex-1 bg-stone-50 dark:bg-zinc-800 rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-stone-900 dark:text-white">{["12.4K", "3.2K", "8.9K"][i]}</p>
                    <p className="text-[10px] text-stone-400 dark:text-zinc-500 uppercase">{m}</p>
                  </div>
                ))}
              </div>
              <div className="h-24 bg-stone-50 dark:bg-zinc-800 rounded-xl flex items-end gap-1 p-3">
                {[40, 65, 45, 80, 55, 90, 70, 85, 60, 75, 95, 50].map((h, i) => (
                  <div key={i} className="flex-1 bg-orange-400 dark:bg-orange-500 rounded-t" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
        </RevealSection>
        <RevealSection direction="left" className="md:w-1/2">
          <h3 className="text-2xl md:text-3xl font-bold text-stone-900 dark:text-white mb-4">Real-Time Analytics Dashboard</h3>
          <p className="text-stone-500 dark:text-zinc-400 leading-relaxed mb-4">
            See how your content performs across every platform in one unified view. Daily trends, engagement metrics, and growth tracking — all in beautiful, interactive charts.
          </p>
          <ul className="space-y-2 text-sm text-stone-600 dark:text-zinc-300">
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Page-level insights per account</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Per-post engagement breakdown</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Customizable date ranges</li>
          </ul>
        </RevealSection>
      </div>

      {/* Block 3 */}
      <div className="flex flex-col md:flex-row items-center gap-12">
        <RevealSection direction="left" className="md:w-1/2">
          <div className="rounded-2xl border border-stone-200/60 dark:border-zinc-800/60 bg-white dark:bg-zinc-900 p-6 shadow-sm">
            {/* Platform overrides mockup */}
            <div className="space-y-2">
              {[
                { platform: "twitter", text: "Check out our latest update! #tech" },
                { platform: "instagram", text: "Swipe to see the full story  #startup #growth" },
                { platform: "linkedin", text: "Excited to share our Q4 results with the team..." },
              ].map(({ platform, text }) => {
                const info = getPlatformInfo(platform);
                return (
                  <div key={platform} className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-zinc-800 rounded-xl">
                    <span className={`w-7 h-7 ${info.color} text-white rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <PlatformIcon platform={platform} className="w-3.5 h-3.5" />
                    </span>
                    <p className="text-xs text-stone-600 dark:text-zinc-300 leading-relaxed">{text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </RevealSection>
        <RevealSection direction="right" className="md:w-1/2">
          <h3 className="text-2xl md:text-3xl font-bold text-stone-900 dark:text-white mb-4">Platform-Specific Customization</h3>
          <p className="text-stone-500 dark:text-zinc-400 leading-relaxed mb-4">
            Override text, hashtags, or media for specific platforms. Tailored content that feels native everywhere — without the manual work.
          </p>
          <ul className="space-y-2 text-sm text-stone-600 dark:text-zinc-300">
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Platform-specific overrides</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Character limit warnings</li>
            <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 flex items-center justify-center text-xs">&#10003;</span>Hashtag management</li>
          </ul>
        </RevealSection>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */

const TESTIMONIALS = [
  {
    quote: "SocialCast saves me 3 hours every day. I used to copy-paste across 6 platforms manually.",
    name: "Sarah Chen",
    role: "Content Creator",
    initial: "S",
    color: "from-pink-400 to-rose-500",
  },
  {
    quote: "The analytics alone are worth it. Finally seeing all my engagement data in one place.",
    name: "Marcus Rivera",
    role: "Social Media Manager",
    initial: "M",
    color: "from-blue-400 to-indigo-500",
  },
  {
    quote: "The API integration was seamless. We automated our entire publishing pipeline in a weekend.",
    name: "Priya Patel",
    role: "Developer",
    initial: "P",
    color: "from-emerald-400 to-teal-500",
  },
];

function TestimonialsSection() {
  return (
    <section className="py-24 bg-stone-50 dark:bg-zinc-900/50 px-4">
      <RevealSection className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white tracking-tight">
          Loved by creators <span className="text-gradient">worldwide</span>
        </h2>
      </RevealSection>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {TESTIMONIALS.map((t, i) => (
          <RevealSection key={i}>
            <div className={`p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-stone-200/60 dark:border-zinc-800/60 h-full flex flex-col reveal-delay-${i + 1}`}>
              <p className="text-5xl text-orange-200 dark:text-orange-500/20 font-serif leading-none mb-2">&ldquo;</p>
              <p className="text-sm text-stone-600 dark:text-zinc-300 leading-relaxed flex-1">{t.quote}</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-stone-100 dark:border-zinc-800">
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center text-white text-sm font-bold`}>
                  {t.initial}
                </div>
                <div>
                  <p className="text-sm font-semibold text-stone-900 dark:text-white">{t.name}</p>
                  <p className="text-xs text-stone-400 dark:text-zinc-500">{t.role}</p>
                </div>
              </div>
            </div>
          </RevealSection>
        ))}
      </div>
    </section>
  );
}

/* ─── Final CTA ─── */

function FinalCTA() {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-orange-500/[0.06] to-transparent dark:from-orange-500/[0.04]" />
      <div className="absolute inset-0 bg-grid" />
      <RevealSection className="relative text-center max-w-2xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-4">
          Ready to simplify your social media?
        </h2>
        <p className="text-stone-500 dark:text-zinc-400 mb-8 text-lg">
          Join thousands of creators publishing smarter with SocialCast.
        </p>
        <Link href="/register" className="inline-block px-8 py-4 bg-orange-500 text-white rounded-2xl font-semibold text-lg hover:bg-orange-600 transition-all animate-pulse-glow shadow-lg shadow-orange-500/20">
          Get Started Free
        </Link>
        <p className="text-xs text-stone-400 dark:text-zinc-600 mt-4">No credit card required</p>
      </RevealSection>
    </section>
  );
}

/* ─── Footer ─── */

function LandingFooter() {
  return (
    <footer className="py-16 border-t border-stone-200/60 dark:border-zinc-800/40 px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/brand/logo-20260908.png" width={28} height={28} alt="" aria-hidden="true" className="w-7 h-7 shrink-0 rounded-lg" />
            <span className="font-bold text-stone-900 dark:text-white text-sm">SocialCast</span>
          </div>
          <p className="text-xs text-stone-400 dark:text-zinc-500 leading-relaxed">
            Unified social media publishing for modern creators.
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold text-stone-900 dark:text-zinc-200 uppercase tracking-widest mb-3">Product</p>
          <div className="space-y-2">
            <Link href="/features" className="block text-sm text-stone-500 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">Features</Link>
            <Link href="/pricing" className="block text-sm text-stone-500 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">Pricing</Link>
            <Link href="/blog" className="block text-sm text-stone-500 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">Blog</Link>
            <Link href="/login" className="block text-sm text-stone-500 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">Sign In</Link>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-stone-900 dark:text-zinc-200 uppercase tracking-widest mb-3">Resources</p>
          <div className="space-y-2">
            <span className="block text-sm text-stone-400 dark:text-zinc-500">API Docs</span>
            <span className="block text-sm text-stone-400 dark:text-zinc-500">Help Center</span>
            <span className="block text-sm text-stone-400 dark:text-zinc-500">Status</span>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-stone-900 dark:text-zinc-200 uppercase tracking-widest mb-3">Legal</p>
          <div className="space-y-2">
            <span className="block text-sm text-stone-400 dark:text-zinc-500">Privacy Policy</span>
            <span className="block text-sm text-stone-400 dark:text-zinc-500">Terms of Service</span>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto mt-12 pt-8 border-t border-stone-200/40 dark:border-zinc-800/40 text-center">
        <p className="text-xs text-stone-400 dark:text-zinc-600">&copy; {new Date().getFullYear()} SocialCast. All rights reserved.</p>
      </div>
    </footer>
  );
}
